import React, {useEffect, useMemo, useState, ChangeEvent, useRef} from "react";
import {useTranslation} from "react-i18next";
import Button from "@sharedComponents/Button";
import {ButtonType} from '@sharedComponents/Button/types'
import InputText from "@sharedComponents/InputText";
import uniqueId from "lodash/uniqueId";
import {EditablePdfField} from "@sharedComponents/EditablePdf/types";
import {GeneratePdfProps} from "@sharedComponents/EditablePdf/GeneratePdf/types";
import "./Scss/generate-pdf.scss";

type FieldValueMap = Record<string, string>;

function fieldKey(f: EditablePdfField) {
  return f.TextInPdfToReplace || "";
}

export const GeneratePdf: React.FC<GeneratePdfProps> = ({
                                                          heading,
                                                          previewUrl,
                                                          previewImageAlt,
                                                          fields,
                                                          loading,
                                                          onDownload,
                                                          children,
                                                          pageWidth
                                                        }) => {
  const {t} = useTranslation();

  const initialValues = useMemo(() => {
    const out: FieldValueMap = {};
    fields.forEach((f) => {
      const key = fieldKey(f);
      if (key) out[key] = "";
    });
    return out;
  }, [fields]);

  const [values, setValues] = useState<FieldValueMap>(initialValues);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const imgRef = useRef<HTMLImageElement | null>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!pageWidth) return;

    const updateScale = () => {
      if (!imgRef.current) return;
      const imgWidth = imgRef.current.clientWidth;
      if (imgWidth > 0) {
        setScale(imgWidth / pageWidth);
      }
    };

    updateScale();

    // update on resize
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, [pageWidth]);

  function handleChange(
    field: EditablePdfField,
    event: ChangeEvent<HTMLInputElement>
  ) {
    const key = fieldKey(field);
    if (!key) return;

    setValues((prev) => ({...prev, [key]: event.target.value}));
  }

  function handleDownload() {
    onDownload(values);
  }

  return (
    <div className="generate-pdf">
      <h3>{t("flyers.create")} {heading}</h3>

      <div className="generate-pdf__preview">
        <div className="generate-pdf__image">
          <img ref={imgRef} src={previewUrl} alt={previewImageAlt}/>
          {/* Overlay preview values at given coordinates */}
          {fields.map((field) => {
            const key = fieldKey(field);
            if (!key) return null;

            const rawFontSize = (field.FontSize ?? 36) * scale;

            const fontSizePx = Math.max(rawFontSize, 16);
            const baselineOffset = fontSizePx * 0.14;

            const style: React.CSSProperties = {
              position: "absolute",
              top: `calc(${field.Y}% - ${baselineOffset}px)`,
              fontSize: `${fontSizePx}px`,
              width: field.Width ? `${field.Width}%` : undefined
            };

            if (field.LeftAligned) {
              // LEFT-ALIGNED PREVIEW
              style.left = `${field.X}%`;
              style.width = `${100 - field.X}%`;

            } else {
              // CENTERED PREVIEW
              style.left = "0";
              style.width = "100%";
            }

            return (
              <div
                key={key}
                className={`generate-pdf__field ${field.LeftAligned ? "generate-pdf__field--left" : ""}`}
                style={style}
              >
                {values[key]}
              </div>
            );
          })}
        </div>

      </div>

      <div className="generate-pdf__field-container">
        {fields.map((field) => {
          const key = fieldKey(field);
          if (!key) return null;

          const label = field.FieldName ?? key;
          const value = values[key] ?? "";

          return (
            <InputText
              key={key}
              id={uniqueId()}
              value={value}
              label={label}
              hideLabel={false}
              disabled={loading}
              onChange={handleChange.bind(null, field)}
              maxLength={50}
            />
          );
        })}
      </div>

      {children}

      <div className="generate-pdf__button-container">
        <Button
          text={t("button.download")}
          type={ButtonType.PRIMARY}
          onClick={handleDownload}
          loading={loading}
          disabled={loading}
          large={true}
        />
      </div>
    </div>
  );
};

export default GeneratePdf;